import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { RedirectFlow } from 'groverPaymentSDK';
import { snowplowPageView } from 'snowplow';

import { STORE_TYPE } from 'ggApp/modules/store/constants';
import {
    isDronesCategory,
    currentProductById as currentProductSelector,
} from 'ggApp/modules/product/selectors';
import { translate } from 'ggApp/modules/translator/selectors';
import { pushDataLayer } from 'ggApp/modules/tracking/actions';
import CriteoDataLayerReporter from 'ggApp/modules/criteo/components/criteoDataLayerReporter';
import criteoEventType from 'ggApp/modules/criteo/eventTypes';
import {
    CfProductDetailPage,
    getPageFromCurrentStore as getContentfulPage,
    fetchPlpPageData,
    CfProductDetailPageForDrones,
} from 'ggApp/modules/contentful';
import { COMMON_QUERY_PARAM, MODAL_QUERY_ACTIVE } from 'ggApp/modules/router/constants';
import {
    TRACKING_COOKIE_NAME,
    KINESIS_STREAM_FIRST_REQUEST,
} from 'ggApp/modules/tracking/constants';
import CheckoutModal from 'ggApp/modules/newCheckout/components/CheckoutModal';
import { AuthModal } from 'ggApp/modules/auth/v3/components';
import { newCheckoutVariants } from 'ggApp/modules/featureFlag';
import flows from 'ggApp/modules/newCheckout/flows';

import CookiesManager from 'ggApp/utils/CookiesManager';
import { getProductCardChips } from 'ggApp/utils/product';
import { OrderContext } from 'ggApp/shared/context/Order/OrderContext';
import { addItemToCart } from 'ggApp/shared/context/Order/helpers/addItemToCart';
import useMount from 'ggApp/shared/hooks/useMount';
import MetaTagsContainer from 'ggApp/shared/containers/metaTags';
import ProductImageOverview from 'ggApp/shared/components/product/imageGallery';
import { Announcement } from 'ggApp/shared/components/Announcement';
import { useProductPreorder } from 'ggApp/shared/hooks/useProductPreorder';
import { formatDateShort } from 'ggApp/utils/date';

import loadConfiguration from 'ggApp/utils/configuration';
import { useVoucher } from './useVoucher';
import BackButton from './BackButton';
import Footer from './Footer';
import { Hr } from './Common';
import { Content as ContentfulContent } from './Content';
import MobileCta from './MobileCta';
import ProductDetails from './ProductDetails';
import SpecialOffer from './SpecialOffer';
import { ProductDescription } from './ProductDescription';
import { PartnerProductPageProps } from './types';

import {
    BackButtonContainer,
    Container,
    Content,
    ContentWrapper,
    Header,
    ProductDetailsContainer,
    ProductImagesContainer,
    StickyContainer,
    StickyCtaContainer,
    Bundles,
    StyledBenefitsSection,
    StyledWhatsInTheBoxSection,
    FeedbackBanner,
    TechSpecs,
    VimeoPlayerWrapper,
    VimeoPlayer,
} from './styled';

const { APP_CONFIG } = loadConfiguration();
const { apiLegacyUrl } = APP_CONFIG;

const PartnerLandingPage = ({
    activeStore,
    dispatch,
    pdpMarketingMaterials: { tooltipText, benefitList = [] } = {
        tooltipText: '',
        benefitList: [],
    },
    contentfulPLPData,
    accessoriesNames = [],
    imageList,
    currentVariant,
    translationKeys = {},
    product,
    selectProductVariant,
    locationQuery,
    trackingActions,
    firstRequestEventData,
    checkoutLandingTracking,
    registerProductDetailedImpression,
    currentRentalPlan,
    selectedRentalPlan,
    registerProductDetailedImpressionV2,
    feedbackBannerText = '',
    feedbackBannerAvailable = false,
    feedbackBannerCopyableText,
    techSpecs = {},
    videoWidget = {
        id: '',
        title: '',
    },
    isIncompletePrivateAccount,
    isIncompleteBusinessAccount,
    isLegacyBusinessAccount,
    removeUrlParams,
    pushUrlParams,
    isLoggedIn,
    initialAuthFlow,
    isFlexibleProduct,
    isValidIdForVideo,
    redirectus,
    newCheckoutEnabled,
    getCFDiscountBanner,
    getCFPartnerDiscountBanner,
    plpBundlesFlag,
    productAnnouncementData,
    rentalPlans,
}: PartnerProductPageProps): JSX.Element => {
    const { send, cart, cartLoaded, newCheckoutFlag } = useContext(OrderContext);
    const [checkoutModal, setCheckoutModal] = useState(null);
    const [authModal, setAuthModal] = useState(null);
    const [authModalFlow, setAuthModalFlow] = useState(initialAuthFlow);
    const [videoFailed, setVideoFailed] = useState(false);
    const [autoRedeemVoucher, setAutoRedeemVoucher] = useState('');

    const [firstFetch, setFirstFetch] = useState(false);

    const [preorderProduct] = useProductPreorder(product.sku, currentVariant.id);

    const { applyVoucher } = useVoucher({
        orderNumber: cart.orderNumber,
        voucher: autoRedeemVoucher,
    });

    const newCheckoutFlow = (): object => {
        if (newCheckoutFlag && newCheckoutFlag.variantKey === newCheckoutVariants.withHomeAddress) {
            return flows.CHECKOUT_WITH_HOME_ADDRESS;
        }
        return flows.CHECKOUT_NO_HOME_ADDRESS;
    };

    const promoChips = useMemo(() => {
        const chips = getProductCardChips({ product, preorderProduct });

        return chips;
    }, [product, preorderProduct]);

    const metaPageInfo = useMemo(
        () => ({
            title: translate(translationKeys, activeStore.activeLanguage, 'METAINFO_HOME_TITLE'),
            description: translate(
                translationKeys,
                activeStore.activeLanguage,
                'METAINFO_HOME_DESCRIPTION',
            ),
            ...(activeStore.store_type === STORE_TYPE.GROVER_PAY && {
                robots: 'noindex, nofollow',
            }),
        }),
        [activeStore.activeLanguage, activeStore.store_type, translationKeys],
    );

    const getContentfulPDPType = useCallback(
        (store) => (isDronesCategory(store) ? CfProductDetailPageForDrones : CfProductDetailPage),
        [],
    );

    const getReturnUrl = useCallback(() => {
        const { variant_id: variantId } = locationQuery;
        const { code } = activeStore;
        return `${apiLegacyUrl}/redirect_back?code=${code}&variant_id=${variantId}`;
    }, [activeStore, locationQuery]);

    const getContentfullPageData = useCallback(() => {
        let language = null;

        if (activeStore) {
            language = activeStore.activeLanguage || activeStore.default_language || 'de';
        }

        dispatch(getContentfulPage(getContentfulPDPType)({ language }));
        dispatch(fetchPlpPageData);
        dispatch(getCFPartnerDiscountBanner);
    }, [activeStore, dispatch, getCFPartnerDiscountBanner, getContentfulPDPType]);

    const setVariant = useCallback(() => {
        const { variant_id: variantId } = locationQuery;
        selectProductVariant(variantId);
    }, [locationQuery, selectProductVariant]);

    const sendTrackingActions = useCallback(() => {
        const cookiesManager = new CookiesManager();
        const groverSession = cookiesManager.get(TRACKING_COOKIE_NAME);
        pushDataLayer(checkoutLandingTracking);

        if (!groverSession) {
            axios.get('/xhr/track').then(() =>
                trackingActions.sendEvent(KINESIS_STREAM_FIRST_REQUEST, {
                    ...firstRequestEventData,
                    session_id: cookiesManager.get(TRACKING_COOKIE_NAME),
                }),
            );
        }
    }, [checkoutLandingTracking, firstRequestEventData, trackingActions]);

    const onOpenAuthModal = useCallback(() => {
        setAuthModalFlow(initialAuthFlow);
        if (authModal) {
            authModal?.openModal();
        }
    }, [authModal, initialAuthFlow]);

    const onOpenCheckoutModal = useCallback(() => {
        if (
            checkoutModal &&
            !isIncompletePrivateAccount &&
            !isIncompleteBusinessAccount &&
            !isLegacyBusinessAccount
        ) {
            checkoutModal?.openModal();
            pushUrlParams({ [COMMON_QUERY_PARAM.checkoutModal]: MODAL_QUERY_ACTIVE });
        }
    }, [
        checkoutModal,
        isIncompletePrivateAccount,
        isIncompleteBusinessAccount,
        isLegacyBusinessAccount,
        pushUrlParams,
    ]);

    const onCloseCheckoutModal = useCallback(() => {
        removeUrlParams([COMMON_QUERY_PARAM.checkoutModal, ...Object.values(RedirectFlow)]);
    }, [removeUrlParams]);

    const onVideoError = useCallback(() => {
        setVideoFailed(true);
    }, []);

    const handleAuthSuccess = useCallback(async () => {
        if (isIncompletePrivateAccount || isIncompleteBusinessAccount || isLegacyBusinessAccount) {
            setAuthModalFlow(initialAuthFlow);
            onOpenAuthModal();
        }

        if (autoRedeemVoucher) {
            try {
                await applyVoucher();
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
            }
        }

        if (!newCheckoutEnabled) {
            redirectus(`/${activeStore.code}/checkout`, true);
        } else {
            onOpenCheckoutModal();
        }
    }, [
        isIncompletePrivateAccount,
        isIncompleteBusinessAccount,
        isLegacyBusinessAccount,
        newCheckoutEnabled,
        initialAuthFlow,
        onOpenAuthModal,
        redirectus,
        activeStore.code,
        onOpenCheckoutModal,
        autoRedeemVoucher,
        applyVoucher,
    ]);

    const ctaClickCallBack = useCallback(
        (callback) => (): void => {
            if (callback && typeof callback === 'function') {
                callback();
            }
            if (
                (!isLoggedIn ||
                    isIncompletePrivateAccount ||
                    isIncompleteBusinessAccount ||
                    isLegacyBusinessAccount) &&
                authModal
            ) {
                setAuthModalFlow(initialAuthFlow);
                onOpenAuthModal();
                return;
            }
            if (!newCheckoutEnabled) {
                redirectus(`/${activeStore.code}/checkout`, true);
            } else {
                onOpenCheckoutModal();
            }
        },
        [
            activeStore.code,
            authModal,
            initialAuthFlow,
            isIncompleteBusinessAccount,
            isIncompletePrivateAccount,
            isLegacyBusinessAccount,
            isLoggedIn,
            newCheckoutEnabled,
            onOpenAuthModal,
            onOpenCheckoutModal,
            redirectus,
        ],
    );

    const handleCtaClick = useCallback(
        ({ items, callback, voucher }) => {
            addItemToCart({
                send,
                cart,
                items,
                callback: ctaClickCallBack(callback),
                voucher,
            });
            if (voucher) {
                setAutoRedeemVoucher(voucher);
            }
        },
        [send, cart, ctaClickCallBack],
    );

    const onCtaClick = useCallback(() => {
        const itemPayload = [
            {
                product,
                variant: currentVariant,
                quantity: 1,
                minimumMonths: selectedRentalPlan.minimumMonths,
            },
        ];

        handleCtaClick({ items: itemPayload });
    }, [currentVariant, handleCtaClick, product, selectedRentalPlan.minimumMonths]);

    const handleCheckoutModalByUrl = useCallback(
        (modal) => {
            const shouldModalOpened =
                !isIncompletePrivateAccount &&
                modal &&
                locationQuery[COMMON_QUERY_PARAM.checkoutModal] === MODAL_QUERY_ACTIVE;

            if (shouldModalOpened) {
                modal.openModal();
            }
        },
        [isIncompletePrivateAccount, locationQuery],
    );

    const createModalRefCheckout = useCallback(
        (modal) => {
            setCheckoutModal(modal);
            handleCheckoutModalByUrl(modal);
        },
        [handleCheckoutModalByUrl],
    );

    const createModalRefAuth = useCallback((modal) => {
        setAuthModal(modal);
    }, []);

    const onCompletedBundle = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (payload: { product: { bundle: any } }) => {
            const { product: productData } = payload || {};
            const hasBundle = Boolean(productData?.bundle);

            if (hasBundle) {
                pushUrlParams({ utm_term: `${currentVariant.sku}_bundle` });
            }
            snowplowPageView();
        },
        [pushUrlParams, currentVariant.sku],
    );

    useMount(() => {
        getContentfullPageData();
        setFirstFetch(true);
        send('FETCH_ORDER');
        sendTrackingActions();
        setVariant();

        registerProductDetailedImpression({
            currentProductSelector,
            productVariantSelector: () => currentVariant,
            rentalPlanSelector: () => currentRentalPlan,
        });
        registerProductDetailedImpressionV2();
        if (!plpBundlesFlag) {
            snowplowPageView();
        }
    });

    useEffect(() => {
        registerProductDetailedImpressionV2();
    }, [rentalPlans, registerProductDetailedImpressionV2]);

    useEffect(() => {
        if (
            checkoutModal &&
            !!Object.keys(cart).length &&
            !cart.items &&
            cartLoaded &&
            locationQuery[COMMON_QUERY_PARAM.waitingModal] !== MODAL_QUERY_ACTIVE
        ) {
            checkoutModal?.closeModal();
        }
    }, [cart, checkoutModal, cartLoaded, locationQuery.waitingModal, locationQuery]);

    return (
        <>
            <Container>
                <MetaTagsContainer metaTags={metaPageInfo} />
                <CriteoDataLayerReporter type={criteoEventType.product} />
                <Header>
                    <BackButtonContainer>
                        <BackButton returnUrl={getReturnUrl()} imageUrl={activeStore.logo_url} />
                    </BackButtonContainer>
                </Header>
                {preorderProduct?.allocationDate ? (
                    <FeedbackBanner
                        textTK="PREORDER_BANNER_TEXT"
                        tkValues={{
                            product: product?.name,
                            date: formatDateShort(preorderProduct.allocationDate),
                        }}
                    />
                ) : null}
                {!preorderProduct && feedbackBannerAvailable && feedbackBannerText ? (
                    <FeedbackBanner
                        text={feedbackBannerText}
                        copyableText={feedbackBannerCopyableText}
                    />
                ) : null}
                <Content>
                    <ProductImagesContainer>
                        <ProductImageOverview imageList={imageList} />
                    </ProductImagesContainer>
                    <ProductDetailsContainer>
                        <StickyContainer>
                            <ProductDetails
                                product={product}
                                preorderProduct={preorderProduct}
                                promoChips={promoChips}
                                tooltipText={tooltipText}
                                onCtaClick={onCtaClick}
                            />
                        </StickyContainer>
                    </ProductDetailsContainer>
                    <ContentWrapper>
                        <SpecialOffer />
                        {plpBundlesFlag ? (
                            <Bundles
                                currentProductId={product?.id}
                                handleCtaClick={handleCtaClick}
                                onCompleted={onCompletedBundle}
                            />
                        ) : null}
                        <StyledBenefitsSection
                            benefitList={benefitList}
                            isFlexibleProduct={isFlexibleProduct}
                        />
                        {productAnnouncementData ? (
                            <Announcement productAnnouncementData={productAnnouncementData} />
                        ) : null}
                        {Array.isArray(accessoriesNames) && accessoriesNames.length ? (
                            <StyledWhatsInTheBoxSection
                                accessoriesNames={accessoriesNames}
                                language={activeStore.activeLanguage}
                            />
                        ) : null}
                        <Hr />
                        {techSpecs ? (
                            <TechSpecs titleTK="PDP_TECH_SPECS_BLOCK_HEADER" list={techSpecs} />
                        ) : null}
                        {isValidIdForVideo ? (
                            <>
                                <VimeoPlayerWrapper hide={videoFailed}>
                                    <VimeoPlayer
                                        config={{ id: videoWidget.id }}
                                        options={{
                                            title: videoWidget.title,
                                        }}
                                        onError={onVideoError}
                                    />
                                </VimeoPlayerWrapper>
                                <Hr />
                            </>
                        ) : null}
                        <ProductDescription />
                        {contentfulPLPData ? (
                            <ContentfulContent content={contentfulPLPData} />
                        ) : null}
                    </ContentWrapper>
                </Content>
                <Hr />
                <Footer />
                <StickyCtaContainer>
                    <MobileCta
                        isAvailable={currentVariant.is_available}
                        onCtaClick={onCtaClick}
                        preorderProduct={preorderProduct}
                    />
                </StickyCtaContainer>
            </Container>
            {newCheckoutFlow ? (
                <>
                    <CheckoutModal
                        flow={newCheckoutFlow}
                        onClose={onCloseCheckoutModal}
                        modalRef={createModalRefCheckout}
                    />
                    <AuthModal
                        modalRef={createModalRefAuth}
                        flow={authModalFlow}
                        onSuccess={handleAuthSuccess}
                        withBackgroundColor
                        options={{ hasSuccessSignupStep: false, isCheckout: true }}
                    />
                </>
            ) : null}
        </>
    );
};

export default PartnerLandingPage;
